import axios from 'axios';

// API base URL configuration
// In development, use relative path so Vite proxy can handle it
// In production, use the full API URL
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV
    ? '' // Use relative path in development (Vite proxy will handle it)
    : 'https://card-api.theliberec.com'); // Full URL in production

const DEFAULT_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT ?? 15000);
const LOGOUT_ON_401 = (import.meta.env.VITE_LOGOUT_ON_401 ?? 'true').toLowerCase() !== 'false';

console.log('API Configuration:', {
  NODE_ENV: import.meta.env.MODE,
  API_BASE_URL,
  TIMEOUT: DEFAULT_TIMEOUT,
  LOGOUT_ON_401
});

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  timeoutErrorMessage: `Request timed out after ${Math.round(DEFAULT_TIMEOUT / 1000)} seconds`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: false
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('merchant_token');
  
  // Don't add Authorization header for login/register endpoints
  if (token && !config.url.includes('/merchants/login') && !config.url.includes('/merchants/register')) {
    config.headers.Authorization = `Bearer ${token}`;
    
    if (config.params && config.params.token) {
      delete config.params.token;
    }
  }
  
  console.log('API Request:', {
    method: config.method?.toUpperCase(),
    url: config.url,
    fullURL: config.baseURL + config.url,
    hasToken: !!token
  });
  
  return config;
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response Success:', {
      status: response.status,
      url: response.config.url
    });
    return response;
  },
  (error) => {
    const { config, code, message, response } = error;
    const timeoutHit = code === 'ECONNABORTED' || message?.toLowerCase().includes('timeout');

    console.error('API Error:', {
      status: response?.status,
      url: config?.url,
      message,
      timeout: timeoutHit
    });

    if (timeoutHit) {
      error.message = 'The request is taking too long. Please try again in a moment.';
    }

    if (LOGOUT_ON_401 && response?.status === 401) {
      // Don't fire logout event if we're already on the login page or if this is a login request
      const isLoginPage = typeof window !== 'undefined' && window.location.pathname === '/login';
      const isLoginRequest = config?.url?.includes('/merchants/login');
      if (typeof window !== 'undefined' && !isLoginPage && !isLoginRequest) {
        window.dispatchEvent(
          new CustomEvent('auth:unauthorized', {
            detail: {
              requestUrl: config?.url,
              status: response.status,
              origin: 'axios'
            }
          })
        );
      }
    }
    return Promise.reject(error);
  }
);

export default api;
export { API_BASE_URL, LOGOUT_ON_401 };

